import React from "react";

import { Typography } from "@mui/material";
import { round } from "lodash";
import { makeStyles } from "tss-react/mui";

import { calculateMacro } from "../utils/macros";

interface MacrosConsumed {
  carbs: number;
  protein: number;
  fat: number;
}

interface Props {
  caloriesConsumed?: number;
  recommendedCalories?: number;
  macrosConsumed?: MacrosConsumed;
}

const TotalCaloriesConsumed: React.FC<Props> = ({
  caloriesConsumed,
  recommendedCalories,
  macrosConsumed,
}) => {
  const { classes, cx } = useStyles();

  const progress = (caloriesConsumed ?? 0) / (recommendedCalories || 1);

  const calculate = calculateMacro(caloriesConsumed ?? 0);
  const macros = macrosConsumed && [
    {
      label: "Carbs",
      grams: round(macrosConsumed.carbs, 1),
      percent: calculate.carbsPercentage(macrosConsumed.carbs),
    },
    {
      label: "Protein",
      grams: round(macrosConsumed.protein, 1),
      percent: calculate.proteinPercentage(macrosConsumed.protein),
    },
    {
      label: "Fat",
      grams: round(macrosConsumed.fat, 1),
      percent: calculate.fatsPercentage(macrosConsumed.fat),
    },
  ];

  return (
    <section className={classes.root}>
      <Typography className={classes.title}>Total Calories Consumed</Typography>
      <meter className={classes.progress} value={progress}></meter>
      <div className={classes.footer}>
        <div>
          <Typography className={classes.text}>Consumed</Typography>
          <Typography className={classes.caloriesConsumed}>
            {round(caloriesConsumed ?? 0)}
          </Typography>
        </div>
        <div>
          <Typography className={cx(classes.text, classes.right)}>
            Goal
          </Typography>
          <Typography
            className={cx(classes.recommendedCalories, classes.right)}
          >
            {round(recommendedCalories ?? 0)}
          </Typography>
        </div>
      </div>
      {macros && (
        <div className={classes.macros}>
          {macros.map((macro) => (
            <div key={macro.label} className={classes.macro}>
              <Typography className={classes.text}>{macro.label}</Typography>
              <Typography className={classes.macroGrams}>
                {macro.grams} g
              </Typography>
              <Typography className={classes.macroPercent}>
                {macro.percent} %
              </Typography>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: "20px 20px 15px",
    backgroundColor: "#fff",
  },
  title: {
    color: "#363E45",
    fontSize: 16,
    fontWeight: 700,
  },
  progress: {
    marginTop: 14,
    marginBottom: 5,
    height: 7,
    width: "100%",
    "&::-webkit-meter-bar": {
      borderRadius: 9999,
      height: 7,
      border: "none",
      backgroundColor: "#EDF0F5",
    },
    "&::-webkit-meter-optimum-value": {
      borderRadius: 9999,
      background:
        "linear-gradient(90deg, rgba(255,186,27,1) 0%, rgba(255,151,44,1) 100%)",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
  },
  text: {
    color: "#4C515A",
    fontSize: 12,
    fontWeight: 600,
  },
  caloriesConsumed: {
    color: "#4C515A",
    fontSize: 16,
    fontWeight: 700,
  },
  recommendedCalories: {
    color: "#4C515A",
    fontSize: 16,
    fontWeight: 700,
  },
  right: {
    textAlign: "right",
  },
  macros: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px solid #EDF0F5",
  },
  macro: {
    display: "flex",
    flexDirection: "column",
  },
  macroGrams: {
    marginTop: 2,
    color: "#363E45",
    fontSize: 14,
    fontWeight: 700,
  },
  macroPercent: {
    color: "#4C515A",
    fontSize: 12,
    fontWeight: 600,
  },
});

export default TotalCaloriesConsumed;
